import { useCallback, useEffect, useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { SignInButton } from '@clerk/clerk-react';
import {
  ArrowLeft,
  ArrowRight,
  BookOpen,
  ChevronDown,
  ChevronUp,
  Check,
  Copy,
  Laptop,
  Loader2,
  MessageSquareText,
  Play,
  RotateCw,
} from 'lucide-react';
import { Eyebrow, MarkerUnderline, RaceBib, Tom } from '@/components/foundry';
import { isClerkAuthConfigured, useAppAuth } from '@/lib/appAuth';
import type { PrimerScreen } from '../lib/workspacePrimerContent';
import {
  postInstallInstructions,
  workspacePrimerScreens,
} from '../lib/workspacePrimerContent';

// ────────────────────────────────────────────────────────────────────────────
// InstallAcquisitionOsCard -- first-moment setup/onboarding surface for
// Builders who have just purchased AI Acquisition OS. Visually anchored in the
// Foundry race-bib motif so this surface reads as "you bought a thing, here's
// the calm first path into it."
//
// State machine:
//
//   primer ──(Create setup message)──> fetching ──┬─ ok ─────> command
//      ▲                                          └─ err ────> token-error
//      │                                                          │
//      └────────── (Back) ─────────────────────────────────────────┘
//      │
//   command ──(Workspace ready)──> installed ──(Start Module 1)──> Build Room
//
// Token fetch hits POST /api/install/aos/token (Clerk auth + os_access
// entitlement; the OS surface caller must be authenticated). The raw setup
// command is wrapped inside a plain-English setup message for Claude Code/Codex.
// ────────────────────────────────────────────────────────────────────────────

type CardState =
  | 'primer'
  | 'fetching-token'
  | 'command'
  | 'token-error'
  | 'auth-error'
  | 'installed';

interface TokenResponse {
  downloadToken: string;
  downloadUrl: string;
  bootstrapUrl: string;
  installCommand: string;
  version: string;
  checksum: string | null;
  expiresAt?: string | null;
  expiresInSeconds: number;
}

type TokenRequestResult =
  | { status: 'ok'; data: TokenResponse }
  | { status: 'auth-expired' }
  | { status: 'error'; message: string };

function buildAiAppSetupMessage(tokenData: TokenResponse) {
  return [
    'Please set up AI Acquisition OS inside my business workspace.',
    '',
    'I am not technical, so guide me in plain English and ask before doing anything risky.',
    '',
    'I have opened the folder I want to use in this app. Install AI Acquisition OS into this folder.',
    '',
    'If you can run commands on my Mac, run this one-time setup line from the folder you have open:',
    tokenData.installCommand,
    '',
    'Do not create a separate AI Brand Lab folder. If this folder is empty and has a generic name like "New project", that is okay. Install here unless I ask to rename it first. I can rename the folder later.',
    '',
    'Only pause before installing if you are not sure which folder is open, if the folder is not empty, or if it looks like an existing unrelated project.',
    '',
    'When setup is finished, do not reply with a technical log first. Give me a warm, customer-facing confirmation that my AI Co-Founder workspace is ready.',
    'Use this structure:',
    '1. Open with: "You\'re in. Your AI Co-Founder workspace is ready."',
    '2. Add one short sentence that makes the moment feel clear and exciting without hype.',
    '3. Give exactly three next steps: return to AI Brand Lab, click Workspace ready, then start Module 1: The Shift.',
    '4. Add one reassurance sentence that there is nothing else to configure right now.',
    '5. Put any path/check details at the bottom under "Technical note", not at the top.',
    'Do not lead with "Workspace is ready", do not write a log-style recap, and do not list things you did not do unless I ask.',
    'Do not start /welcome yet. Module 1 explains what that conversation is and when to run it.',
    'Important: slash skills load when a new Claude Code or Codex session starts. After installing the files, tell me to start a fresh chat/session in this same folder before looking for /welcome.',
  ].join('\n');
}

const aiAppSetupPreview = [
  'Set up AI Acquisition OS inside my business workspace.',
  'Guide me in plain English.',
  'Use the folder I already opened in this app.',
  'Generic empty folder names are okay.',
  'When setup is finished, tell me the workspace is ready.',
  'Then I will start a fresh chat in this folder.',
  'Module 1 will explain /welcome next.',
].join('\n');

/** localStorage key -- persists install completion across sessions. */
export const AIOS_INSTALL_COMPLETED_KEY = 'abl:aios-install-completed';

interface InstallAcquisitionOsCardProps {
  /** Defaults to true. Set false to hard-hide for non-entitled Builders. */
  visible?: boolean;
  /** Local persistence key. Parents can scope this by authenticated user id. */
  storageKey?: string;
  /**
   * Fires whenever the Builder transitions in or out of the installed state.
   * Lets the parent surface (Core.tsx) gate the AI Acquisition OS product
   * card CTA between setup-first and Build Room-ready states.
   */
  onInstallStateChange?: (installed: boolean) => void;
  /** Opens the first Build Room module after the workspace is ready. */
  onStartModuleOne?: () => void;
}

export default function InstallAcquisitionOsCard({
  visible = true,
  storageKey = AIOS_INSTALL_COMPLETED_KEY,
  onInstallStateChange,
  onStartModuleOne,
}: InstallAcquisitionOsCardProps) {
  const reduceMotion = useReducedMotion();
  const { getToken, isLoaded, isSignedIn } = useAppAuth();

  // Restore previously-installed state from localStorage so a Builder who
  // closed the tab after running the command lands back in the installed
  // surface, not at primer screen 1.
  const initialCardState: CardState =
    typeof window !== 'undefined' &&
    window.localStorage.getItem(storageKey) === '1'
      ? 'installed'
      : 'primer';

  const [cardState, setCardState] = useState<CardState>(initialCardState);
  const [primerIndex, setPrimerIndex] = useState(0);
  const [tokenData, setTokenData] = useState<TokenResponse | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [copyState, setCopyState] = useState<'idle' | 'copying' | 'copied' | 'selected'>('idle');
  const [isSetupExpanded, setIsSetupExpanded] = useState(() => initialCardState !== 'installed');

  // Notify the parent on mount if we restored an installed state, so the
  // Core.tsx ProductFeatureCard CTA can render correctly without waiting
  // for the next state transition.
  useEffect(() => {
    if (initialCardState === 'installed') {
      onInstallStateChange?.(true);
    }
    // Run only on mount -- initialCardState is captured at that point.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const markInstalled = useCallback(() => {
    setCardState('installed');
    setIsSetupExpanded(false);
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(storageKey, '1');
    }
    onInstallStateChange?.(true);
  }, [onInstallStateChange, storageKey]);

  const resetCard = useCallback(() => {
    setPrimerIndex(0);
    setTokenData(null);
    setErrorMessage(null);
    setCardState('primer');
    setIsSetupExpanded(true);
    if (typeof window !== 'undefined') {
      window.localStorage.removeItem(storageKey);
    }
    onInstallStateChange?.(false);
  }, [onInstallStateChange, storageKey]);

  const fetchInstallToken = useCallback(async (): Promise<TokenRequestResult> => {
    try {
      const authToken = await getToken({ template: 'convex' }).catch(() => undefined);
      const res = await fetch('/api/install/aos/token', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
        },
        credentials: 'include',
      });
      // 401 is a known recoverable state: the Builder's session expired
      // while the page was open. Show a sign-back-in prompt, not a generic
      // "something went wrong" message that suggests a server issue.
      if (res.status === 401) {
        return { status: 'auth-expired' };
      }
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as {
          error?: string;
          requestId?: string;
        };
        const detail = body?.requestId
          ? `${body.error || `Request failed (${res.status})`} (${body.requestId})`
          : body?.error || `Request failed (${res.status})`;
        throw new Error(detail);
      }
      const data = (await res.json()) as TokenResponse;
      return { status: 'ok', data };
    } catch (err) {
      return {
        status: 'error',
        message:
          err instanceof Error
            ? err.message
            : 'Something went wrong reaching the install service.',
      };
    }
  }, [getToken]);

  const applyTokenResult = useCallback((result: TokenRequestResult) => {
    if (result.status === 'auth-expired') {
      setCardState('auth-error');
      return null;
    }
    if (result.status === 'error') {
      setErrorMessage(result.message);
      setCardState('token-error');
      return null;
    }
    setTokenData(result.data);
    return result.data;
  }, []);

  const requestInstallCommand = useCallback(async () => {
    if (!isSignedIn) {
      setCardState('auth-error');
      return;
    }

    setCardState('fetching-token');
    setErrorMessage(null);
    const data = applyTokenResult(await fetchInstallToken());
    if (data) setCardState('command');
  }, [applyTokenResult, fetchInstallToken, isSignedIn]);

  const handleCopy = useCallback(async () => {
    if (!tokenData) return;
    setCopyState('copying');
    const freshTokenData = applyTokenResult(await fetchInstallToken());
    if (!freshTokenData) {
      setCopyState('idle');
      return;
    }
    try {
      await navigator.clipboard.writeText(buildAiAppSetupMessage(freshTokenData));
      setCopyState('copied');
      setTimeout(() => setCopyState('idle'), 2000);
    } catch {
      // Older browsers without async clipboard: select the visible preview
      // so the Builder can still copy the plain-English part manually.
      const el = document.getElementById('aios-setup-message-preview');
      if (el) {
        const range = document.createRange();
        range.selectNodeContents(el);
        const sel = window.getSelection();
        if (sel) {
          sel.removeAllRanges();
          sel.addRange(range);
        }
        setCopyState('selected');
        setTimeout(() => setCopyState('idle'), 3500);
      }
    }
  }, [applyTokenResult, fetchInstallToken, tokenData]);

  if (!visible) return null;

  const isWaitingForAuth = !isLoaded;
  const isSignedOut = isLoaded && !isSignedIn;
  const railState = isSignedOut || isWaitingForAuth ? 'auth-error' : cardState;
  const isCompactInstalled =
    cardState === 'installed' && !isSetupExpanded && !isSignedOut && !isWaitingForAuth;

  if (isCompactInstalled) {
    return (
      <CompactInstalledSurface
        reduceMotion={Boolean(reduceMotion)}
        onExpand={() => setIsSetupExpanded(true)}
        onReset={resetCard}
        onStartModuleOne={onStartModuleOne}
      />
    );
  }

  return (
    <section
      aria-labelledby="aios-install-heading"
      className="relative mb-8"
    >
      <div className="mb-4 flex flex-wrap items-end justify-between gap-3 border-b border-fd-border pb-3">
        <div>
          <Eyebrow id="aios-install-heading" className="mb-1 block text-fd-accent-deep">
            setup · your workspace
          </Eyebrow>
          <h2 className="font-geist text-[22px] font-medium leading-[1.1] tracking-[-0.02em] text-fd-text">
            Set up AI Acquisition OS in your business folder.
          </h2>
        </div>
        <p className="max-w-[340px] font-caveat text-[18px] leading-[1.1] text-fd-text-muted">
          Watch the welcome, choose your AI app, create the workspace, then start Module 1.
        </p>
      </div>

      <motion.div
        initial={reduceMotion ? false : { opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ type: 'spring', stiffness: 120, damping: 22 }}
        className="relative isolate overflow-hidden rounded-[14px] border border-fd-border-strong/40 bg-fd-paper shadow-[0_20px_40px_-25px_rgba(20,16,8,0.18)]"
      >
        {/* Race-bib stripe along the top of the card -- brand IP-lock. */}
        <div
          aria-hidden="true"
          className="absolute inset-x-0 top-0 h-[6px] bg-fd-accent"
        />

        <div className="grid grid-cols-1 gap-0 md:grid-cols-[1fr_280px]">
          {/* Left rail: stateful content ───────────────────────────────── */}
          <div className="p-6 md:p-8 lg:p-9">
            {isWaitingForAuth ? (
              <AuthLoadingSurface reduceMotion={Boolean(reduceMotion)} />
            ) : isSignedOut ? (
              <SignedOutInstallSurface reduceMotion={Boolean(reduceMotion)} />
            ) : (
              <AnimatePresence mode="wait" initial={false}>
                {cardState === 'primer' && (
                  <PrimerSurface
                    key={`primer-${primerIndex}`}
                    reduceMotion={Boolean(reduceMotion)}
                    index={primerIndex}
                    onNext={() => {
                      if (primerIndex < workspacePrimerScreens.length - 1) {
                        setPrimerIndex(primerIndex + 1);
                      } else {
                        void requestInstallCommand();
                      }
                    }}
                    onBack={() => primerIndex > 0 && setPrimerIndex(primerIndex - 1)}
                  />
                )}

                {cardState === 'fetching-token' && (
                  <FetchingSurface key="fetching" reduceMotion={Boolean(reduceMotion)} />
                )}

                {cardState === 'command' && tokenData && (
                  <CommandSurface
                    key="command"
                    reduceMotion={Boolean(reduceMotion)}
                    tokenData={tokenData}
                    copyState={copyState}
                    onCopy={handleCopy}
                    onBack={() => setCardState('primer')}
                    onMarkInstalled={markInstalled}
                  />
                )}

                {cardState === 'token-error' && (
                  <TokenErrorSurface
                    key="error"
                    reduceMotion={Boolean(reduceMotion)}
                    message={errorMessage ?? 'Something went wrong.'}
                    onRetry={() => void requestInstallCommand()}
                    onBack={() => setCardState('primer')}
                  />
                )}

                {cardState === 'auth-error' && (
                  <AuthErrorSurface
                    key="auth-error"
                    reduceMotion={Boolean(reduceMotion)}
                    onRetry={() => void requestInstallCommand()}
                    onBack={() => setCardState('primer')}
                  />
                )}

                {cardState === 'installed' && (
                  <InstalledSurface
                    key="installed"
                    reduceMotion={Boolean(reduceMotion)}
                    onCollapse={() => setIsSetupExpanded(false)}
                    onReset={resetCard}
                    onStartModuleOne={onStartModuleOne}
                  />
                )}
              </AnimatePresence>
            )}
          </div>

          {/* Right rail: progress + character anchor ──────────────────── */}
          <aside
            aria-hidden={railState !== 'primer' && !isSignedOut && !isWaitingForAuth}
            className="border-t border-fd-border bg-fd-surface-1/40 p-6 md:border-l md:border-t-0 md:p-7"
          >
            <ProgressRail cardState={railState} primerIndex={primerIndex} />
            <div className="mt-6 hidden md:block">
              <Tom variant="welcoming" className="h-32 w-auto opacity-95" />
            </div>
            <p className="mt-3 hidden font-caveat text-[15px] leading-[1.15] text-fd-text-muted md:block">
              {isSignedOut
                ? 'Sign in with the email you used at checkout and your setup message appears here.'
                : isWaitingForAuth
                  ? 'Checking your Lab session before showing the setup message.'
                  : railNoteFor(cardState, primerIndex)}
            </p>
          </aside>
        </div>
      </motion.div>
    </section>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Surface: visible signed-out install gate
// ────────────────────────────────────────────────────────────────────────────

function AuthLoadingSurface({ reduceMotion }: { reduceMotion: boolean }) {
  return (
    <motion.div
      initial={reduceMotion ? false : { opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.18 }}
    >
      <Eyebrow className="mb-2 block text-fd-accent-deep">checking your account</Eyebrow>
      <h3 className="font-geist text-[26px] font-medium leading-[1.1] tracking-[-0.02em] text-fd-text">
        Your workspace setup lives here.
      </h3>
      <div className="mt-6 flex items-center gap-3 rounded-[10px] border border-fd-border bg-fd-surface-1/40 px-4 py-3 text-[13px] text-fd-text-muted">
        <Loader2 className="size-4 animate-spin text-fd-accent-deep" aria-hidden="true" />
        Checking whether you are signed in before showing the setup message.
      </div>
    </motion.div>
  );
}

function SignedOutInstallSurface({ reduceMotion }: { reduceMotion: boolean }) {
  return (
    <motion.div
      initial={reduceMotion ? false : { opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ type: 'spring', stiffness: 140, damping: 22 }}
    >
      <Eyebrow className="mb-2 block text-fd-accent-deep">setup ready</Eyebrow>
      <h3 className="font-geist text-[26px] font-medium leading-[1.1] tracking-[-0.02em] text-fd-text">
        Sign in to set up AI Acquisition OS.
      </h3>
      <p className="mt-4 max-w-[58ch] text-[15px] leading-[1.55] text-fd-text-muted">
        This setup card stays in your Build Room permanently, so you can create the
        workspace, reinstall it, or start a fresh one later. Sign in with the
        email you used at checkout and the one-time setup message will appear here.
      </p>

      <div className="mt-5 rounded-[10px] border border-fd-border bg-fd-surface-1/50 px-4 py-3">
        <div className="flex items-start gap-3">
          <RaceBib n="01" tone="race" size="sm" />
          <div>
            <span className="block font-geist text-[14px] font-medium text-fd-text">
              Your setup message is tied to paid AI Acquisition OS access.
            </span>
            <p className="mt-1 text-[13px] leading-[1.5] text-fd-text-muted">
              The setup message is generated after sign-in so the private download link stays
              tied to your account.
            </p>
          </div>
        </div>
      </div>

      <div className="mt-6">
        {isClerkAuthConfigured ? (
          <SignInButton
            mode="modal"
            fallbackRedirectUrl={currentHref()}
            signUpFallbackRedirectUrl={currentHref()}
          >
            <button
              type="button"
              className="inline-flex items-center gap-1.5 rounded-fd-sm bg-fd-accent px-4 py-2 font-geist text-[13px] font-semibold text-[oklch(0.16_0.005_95)] shadow-[0_1px_0_rgba(0,0,0,0.04)] transition-transform hover:-translate-y-px active:translate-y-0"
            >
              Sign in to set up
              <ArrowRight className="size-3.5" aria-hidden="true" />
            </button>
          </SignInButton>
        ) : (
          <button
            type="button"
            disabled
            className="inline-flex items-center gap-1.5 rounded-fd-sm border border-fd-border bg-fd-surface-1 px-4 py-2 font-geist text-[13px] font-medium text-fd-text-subtle"
          >
            Sign in unavailable
          </button>
        )}
      </div>
    </motion.div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Surface: primer walk-through
// ────────────────────────────────────────────────────────────────────────────

interface PrimerSurfaceProps {
  reduceMotion: boolean;
  index: number;
  onNext: () => void;
  onBack: () => void;
}

function PrimerSurface({ reduceMotion, index, onNext, onBack }: PrimerSurfaceProps) {
  const screen = workspacePrimerScreens[index];
  const isLast = index === workspacePrimerScreens.length - 1;
  const isFirst = index === 0;

  return (
    <motion.div
      initial={reduceMotion ? false : { opacity: 0, x: 8 }}
      animate={{ opacity: 1, x: 0 }}
      exit={reduceMotion ? undefined : { opacity: 0, x: -8 }}
      transition={{ type: 'spring', stiffness: 140, damping: 22 }}
    >
      <Eyebrow className="mb-2 block text-fd-accent-deep">{screen.eyebrow}</Eyebrow>
      <h3 className="font-geist text-[26px] font-medium leading-[1.1] tracking-[-0.02em] text-fd-text">
        {screen.title}
      </h3>

      {screen.video ? <PrimerVideoPlaceholder video={screen.video} /> : null}

      <div className="mt-5 max-w-[58ch] space-y-3 text-[15px] leading-[1.55] text-fd-text-muted">
        {screen.body.map((paragraph, i) => (
          <PrimerParagraph key={i} text={paragraph} />
        ))}
      </div>

      {screen.cards ? <PrimerCardGrid cards={screen.cards} /> : null}

      {screen.callout && (
        <aside className="mt-5 rounded-[10px] border border-fd-border bg-fd-surface-1/60 px-4 py-3">
          <span className="block text-[11px] font-medium uppercase tracking-[0.16em] text-fd-accent-deep">
            {screen.callout.label}
          </span>
          <p className="mt-1 text-[14px] leading-[1.55] text-fd-text">
            {screen.callout.text}
          </p>
        </aside>
      )}

      <div className="mt-6 flex flex-wrap items-center justify-between gap-3">
        <ScreenCounter index={index} total={workspacePrimerScreens.length} />
        <div className="flex items-center gap-2">
          {!isFirst && screen.secondaryLabel && (
            <button
              type="button"
              onClick={onBack}
              className="inline-flex items-center gap-1.5 rounded-fd-sm border border-fd-border bg-transparent px-3 py-2 font-geist text-[13px] font-medium text-fd-text-muted transition-colors hover:bg-fd-surface-1 hover:text-fd-text"
            >
              <ArrowLeft className="size-3.5" aria-hidden="true" />
              {screen.secondaryLabel}
            </button>
          )}
          <button
            type="button"
            onClick={onNext}
            className="inline-flex items-center gap-1.5 rounded-fd-sm bg-fd-accent px-4 py-2 font-geist text-[13px] font-semibold text-[oklch(0.16_0.005_95)] shadow-[0_1px_0_rgba(0,0,0,0.04)] transition-transform hover:-translate-y-px active:translate-y-0"
          >
            {screen.primaryLabel}
            {isLast ? (
              <MessageSquareText className="size-3.5" aria-hidden="true" />
            ) : (
              <ArrowRight className="size-3.5" aria-hidden="true" />
            )}
          </button>
        </div>
      </div>
    </motion.div>
  );
}

function PrimerVideoPlaceholder({
  video,
}: {
  video: NonNullable<PrimerScreen['video']>;
}) {
  return (
    <div className="mt-5 overflow-hidden rounded-[10px] border border-fd-border-strong/40 bg-[oklch(0.17_0.006_95)] shadow-[0_14px_30px_-22px_rgba(20,16,8,0.32)]">
      <div className="relative aspect-video bg-[linear-gradient(135deg,oklch(0.22_0.012_95),oklch(0.12_0.005_95))]">
        <div
          aria-hidden="true"
          className="absolute inset-0 opacity-[0.14]"
          style={{
            backgroundImage:
              'linear-gradient(rgba(255,255,255,0.26) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.26) 1px, transparent 1px)',
            backgroundSize: '28px 28px',
          }}
        />
        <div className="absolute left-4 top-4">
          <RaceBib n="01" tone="race" size="sm" />
        </div>
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="inline-flex size-14 items-center justify-center rounded-full border border-fd-accent/70 bg-fd-accent text-[oklch(0.16_0.005_95)] shadow-[0_10px_22px_-14px_rgba(255,221,36,0.8)]">
            <Play className="ml-0.5 size-6 fill-current" aria-hidden="true" />
          </span>
        </div>
        <span className="absolute bottom-4 right-4 rounded-[6px] border border-white/20 bg-black/40 px-2 py-1 font-mono text-[10px] uppercase tracking-[0.14em] text-white/80">
          {video.duration}
        </span>
      </div>
      <div className="bg-fd-paper px-4 py-3">
        <span className="block text-[10px] font-medium uppercase tracking-[0.16em] text-fd-accent-deep">
          {video.label}
        </span>
        <p className="mt-1 font-geist text-[15px] font-medium leading-[1.25] text-fd-text">
          {video.title}
        </p>
        <p className="mt-1 text-[13px] leading-[1.45] text-fd-text-muted">
          {video.description}
        </p>
      </div>
    </div>
  );
}

function PrimerCardGrid({
  cards,
}: {
  cards: NonNullable<PrimerScreen['cards']>;
}) {
  return (
    <ul className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-2">
      {cards.map((card, i) => (
        <li
          key={card.title}
          className="rounded-[10px] border border-fd-border bg-fd-surface-1/45 p-4"
        >
          <div className="flex items-start gap-3">
            <span className="inline-flex size-8 shrink-0 items-center justify-center rounded-[8px] border border-fd-border bg-fd-paper text-fd-accent-deep">
              <Laptop className="size-4" aria-hidden="true" />
            </span>
            <div className="min-w-0">
              <span className="block font-mono text-[10px] uppercase tracking-[0.16em] text-fd-accent-deep">
                {card.label || `Option ${String(i + 1).padStart(2, '0')}`}
              </span>
              <p className="mt-1 font-geist text-[14px] font-medium leading-[1.25] text-fd-text">
                {card.title}
              </p>
              <p className="mt-1.5 text-[13px] leading-[1.45] text-fd-text-muted">
                {card.body}
              </p>
            </div>
          </div>
        </li>
      ))}
    </ul>
  );
}

/**
 * Renders a primer paragraph, parsing **bold** segments inline.
 * Intentionally limited -- full Markdown would be overkill for this walkthrough.
 */
function PrimerParagraph({ text }: { text: string }) {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return (
    <p>
      {parts.map((part, i) => {
        if (part.startsWith('**') && part.endsWith('**')) {
          return (
            <strong key={i} className="font-medium text-fd-text">
              {part.slice(2, -2)}
            </strong>
          );
        }
        return <span key={i}>{part}</span>;
      })}
    </p>
  );
}

function ScreenCounter({ index, total }: { index: number; total: number }) {
  return (
    <span className="font-mono text-[11px] uppercase tracking-[0.18em] text-fd-text-subtle">
      {String(index + 1).padStart(2, '0')} <span className="opacity-50">/</span>{' '}
      {String(total).padStart(2, '0')}
    </span>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Surface: token in-flight (skeleton)
// ────────────────────────────────────────────────────────────────────────────

function FetchingSurface({ reduceMotion }: { reduceMotion: boolean }) {
  return (
    <motion.div
      initial={reduceMotion ? false : { opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.18 }}
    >
      <Eyebrow className="mb-2 block text-fd-accent-deep">getting your setup message</Eyebrow>
      <h3 className="font-geist text-[26px] font-medium leading-[1.1] tracking-[-0.02em] text-fd-text">
        One second --
      </h3>

      <div className="mt-6 flex items-center gap-3 rounded-[10px] border border-fd-border bg-fd-surface-1/40 px-4 py-3 text-[13px] text-fd-text-muted">
        <Loader2 className="size-4 animate-spin text-fd-accent-deep" aria-hidden="true" />
        Preparing a one-time setup message for your account.
      </div>

      <div
        className="mt-3 h-[44px] w-full animate-pulse rounded-[10px] border border-fd-border bg-fd-surface-1/40"
        aria-hidden="true"
      />

      <p className="mt-4 text-[13px] leading-[1.5] text-fd-text-subtle">
        The private setup link is refreshed when you copy the message, so the link you paste
        into Claude Code or Codex is fresh.
      </p>
    </motion.div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Surface: AI-app setup message + post-setup steps
// ────────────────────────────────────────────────────────────────────────────

interface CommandSurfaceProps {
  reduceMotion: boolean;
  tokenData: TokenResponse;
  copyState: 'idle' | 'copying' | 'copied' | 'selected';
  onCopy: () => void;
  onBack: () => void;
  onMarkInstalled: () => void;
}

function CommandSurface({
  reduceMotion,
  tokenData,
  copyState,
  onCopy,
  onBack,
  onMarkInstalled,
}: CommandSurfaceProps) {
  const expiresLabel = formatExpiryDuration(tokenData.expiresInSeconds);

  return (
    <motion.div
      initial={reduceMotion ? false : { opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={reduceMotion ? undefined : { opacity: 0, y: -8 }}
      transition={{ type: 'spring', stiffness: 140, damping: 22 }}
    >
      <Eyebrow className="mb-2 block text-fd-accent-deep">your setup message</Eyebrow>
      <h3 className="font-geist text-[26px] font-medium leading-[1.1] tracking-[-0.02em] text-fd-text">
        Paste this into{' '}
        <MarkerUnderline tone="race" strokeWidth={2} duration={700} delay={150}>
          Claude Code or Codex
        </MarkerUnderline>
      </h3>
      <p className="mt-3 max-w-[52ch] text-[14px] leading-[1.55] text-fd-text-muted">
        Open Claude Code or Codex Desktop, start a new chat, paste the setup message, and let
        the AI app guide you. When it finishes, start one fresh chat in the same folder so the
        new slash skills appear.
      </p>

      <div className="mt-5 rounded-[12px] border border-fd-border-strong/40 bg-fd-surface-1/50 p-4 shadow-[0_10px_24px_-20px_rgba(20,16,8,0.2)]">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start">
          <div className="inline-flex size-9 shrink-0 items-center justify-center rounded-[8px] border border-fd-border bg-fd-paper text-fd-accent-deep">
            <MessageSquareText className="size-4" aria-hidden="true" />
          </div>
          <div className="min-w-0 grow">
            <span className="block font-geist text-[14px] font-medium leading-[1.25] text-fd-text">
              Message preview
            </span>
            <pre
              id="aios-setup-message-preview"
              className="mt-2 whitespace-pre-wrap font-geist text-[13.5px] leading-[1.55] text-fd-text-muted"
            >
              {aiAppSetupPreview}
            </pre>
            <p className="mt-3 text-[12.5px] leading-[1.45] text-fd-text-subtle">
              The copied message includes your private setup link. You do not need to read or
              edit the technical line inside it.
            </p>
            {copyState === 'selected' ? (
              <p className="mt-2 rounded-[8px] border border-fd-border bg-fd-paper px-3 py-2 text-[12.5px] leading-[1.4] text-fd-text">
                The message is selected. Press Cmd+C, then paste it into Claude Code or Codex.
              </p>
            ) : null}
          </div>
          <button
            type="button"
            onClick={onCopy}
            disabled={copyState === 'copying'}
            className="inline-flex shrink-0 items-center justify-center gap-1.5 rounded-fd-sm bg-fd-accent px-3 py-2 font-geist text-[12.5px] font-semibold text-[oklch(0.16_0.005_95)] shadow-[0_1px_0_rgba(0,0,0,0.04)] transition-transform hover:-translate-y-px active:translate-y-0"
            aria-label="Copy setup message"
          >
            {copyState === 'copying' ? (
              <>
                <Loader2 className="size-3.5 animate-spin" aria-hidden="true" />
                Refreshing
              </>
            ) : copyState === 'copied' ? (
              <>
                <Check className="size-3.5" aria-hidden="true" />
                Copied
              </>
            ) : copyState === 'selected' ? (
              <>
                <Check className="size-3.5" aria-hidden="true" />
                Selected
              </>
            ) : (
              <>
                <Copy className="size-3.5" aria-hidden="true" />
                Copy setup message
              </>
            )}
          </button>
        </div>
        <details className="mt-4 rounded-[8px] border border-fd-border bg-fd-paper px-3 py-2 text-[12.5px] text-fd-text-muted">
          <summary className="cursor-pointer font-geist text-[12px] font-medium text-fd-text-subtle">
            Technical fallback for support
          </summary>
          <code className="mt-2 block overflow-x-auto whitespace-pre-wrap break-all font-mono text-[11.5px] leading-[1.5] text-fd-text-subtle">
            {tokenData.installCommand}
          </code>
        </details>
      </div>

      <ul className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-3">
        {postInstallInstructions.steps.map((step, i) => (
          <li
            key={step.label}
            className="rounded-[10px] border border-fd-border bg-fd-surface-1/40 p-3"
          >
            <RaceBib n={String(i + 1).padStart(2, '0')} tone="race" size="sm" />
            <p className="mt-2 text-[12.5px] leading-[1.5] text-fd-text-muted">{step.body}</p>
          </li>
        ))}
      </ul>

      <div className="mt-5 rounded-[10px] border border-fd-border bg-fd-surface-1/30 px-3 py-2.5">
        <span className="block text-[11px] font-medium uppercase tracking-[0.16em] text-fd-text-subtle">
          {postInstallInstructions.troubleshooting.title}
        </span>
        <p className="mt-1 text-[13px] leading-[1.5] text-fd-text-muted">
          {postInstallInstructions.troubleshooting.text}
        </p>
      </div>

      <div className="mt-6 flex flex-wrap items-center justify-between gap-3">
        <button
          type="button"
          onClick={onBack}
          className="inline-flex items-center gap-1.5 rounded-fd-sm border border-fd-border bg-transparent px-3 py-2 font-geist text-[13px] font-medium text-fd-text-muted transition-colors hover:bg-fd-surface-1 hover:text-fd-text"
        >
          <ArrowLeft className="size-3.5" aria-hidden="true" />
          Back to the primer
        </button>
        <button
          type="button"
          onClick={onMarkInstalled}
          className="inline-flex items-center gap-1.5 rounded-fd-sm border border-fd-border bg-fd-surface-1 px-4 py-2 font-geist text-[13px] font-medium text-fd-text transition-colors hover:bg-fd-surface-2"
        >
          <Check className="size-3.5" aria-hidden="true" />
          Workspace ready
        </button>
      </div>

      <p className="mt-3 text-[12px] leading-[1.45] text-fd-text-subtle">
        The setup link refreshes every time you copy it. Latest link expires in {expiresLabel}.
        Build {tokenData.version}.
      </p>
    </motion.div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Surface: token issuance failed
// ────────────────────────────────────────────────────────────────────────────

interface TokenErrorSurfaceProps {
  reduceMotion: boolean;
  message: string;
  onRetry: () => void;
  onBack: () => void;
}

function TokenErrorSurface({ reduceMotion, message, onRetry, onBack }: TokenErrorSurfaceProps) {
  return (
    <motion.div
      initial={reduceMotion ? false : { opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ type: 'spring', stiffness: 140, damping: 22 }}
    >
      <Eyebrow className="mb-2 block text-fd-coral">we couldn&rsquo;t create the setup message</Eyebrow>
      <h3 className="font-geist text-[24px] font-medium leading-[1.15] tracking-[-0.01em] text-fd-text">
        Hmm. Let&rsquo;s try that again.
      </h3>

      <p className="mt-4 max-w-[52ch] text-[14px] leading-[1.55] text-fd-text-muted">
        Most often this is a quick blip on our side. Reload and try once more; if it keeps
        happening, message us in the Lab and we&rsquo;ll create the setup message with you by
        hand.
      </p>

      <div className="mt-5 rounded-[10px] border border-fd-border bg-fd-surface-1/40 px-4 py-3 font-mono text-[12px] text-fd-text-subtle">
        {message}
      </div>

      <div className="mt-5 flex flex-wrap items-center gap-3">
        <button
          type="button"
          onClick={onRetry}
          className="inline-flex items-center gap-1.5 rounded-fd-sm bg-fd-accent px-4 py-2 font-geist text-[13px] font-semibold text-[oklch(0.16_0.005_95)] transition-transform hover:-translate-y-px active:translate-y-0"
        >
          <RotateCw className="size-3.5" aria-hidden="true" />
          Try again
        </button>
        <button
          type="button"
          onClick={onBack}
          className="inline-flex items-center gap-1.5 rounded-fd-sm border border-fd-border bg-transparent px-3 py-2 font-geist text-[13px] font-medium text-fd-text-muted transition-colors hover:bg-fd-surface-1 hover:text-fd-text"
        >
          <ArrowLeft className="size-3.5" aria-hidden="true" />
          Back to the primer
        </button>
      </div>
    </motion.div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Surface: session expired / not signed in
//
// This fires when the install token endpoint returns 401, which means the
// Builder's Clerk session has expired while the OS page was open. It's a
// recoverable state, not a server problem -- the right move is sign-back-in,
// not retry. Surfaced separately so the copy doesn't blame "our side" for
// what's actually an expired session.
// ────────────────────────────────────────────────────────────────────────────

interface AuthErrorSurfaceProps {
  reduceMotion: boolean;
  onRetry: () => void;
  onBack: () => void;
}

function AuthErrorSurface({ reduceMotion, onRetry, onBack }: AuthErrorSurfaceProps) {
  return (
    <motion.div
      initial={reduceMotion ? false : { opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ type: 'spring', stiffness: 140, damping: 22 }}
    >
      <Eyebrow className="mb-2 block text-fd-coral">looks like your session expired</Eyebrow>
      <h3 className="font-geist text-[24px] font-medium leading-[1.15] tracking-[-0.01em] text-fd-text">
        Sign back in and we&rsquo;ll pick up here.
      </h3>

      <p className="mt-4 max-w-[55ch] text-[14px] leading-[1.55] text-fd-text-muted">
        Your sign-in timed out while this page was open -- not unusual if the tab has been
        sitting for a while. Refresh the page or sign back in, then come straight back to this
        card. Nothing&rsquo;s lost; the workspace setup hasn&rsquo;t started yet.
      </p>

      <div className="mt-5 flex flex-wrap items-center gap-3">
        <button
          type="button"
          onClick={() => {
            if (typeof window !== 'undefined') window.location.reload();
          }}
          className="inline-flex items-center gap-1.5 rounded-fd-sm bg-fd-accent px-4 py-2 font-geist text-[13px] font-semibold text-[oklch(0.16_0.005_95)] transition-transform hover:-translate-y-px active:translate-y-0"
        >
          <RotateCw className="size-3.5" aria-hidden="true" />
          Refresh the page
        </button>
        <button
          type="button"
          onClick={onRetry}
          className="inline-flex items-center gap-1.5 rounded-fd-sm border border-fd-border bg-transparent px-3 py-2 font-geist text-[13px] font-medium text-fd-text transition-colors hover:bg-fd-surface-1"
        >
          I&rsquo;m signed back in -- try again
        </button>
        <button
          type="button"
          onClick={onBack}
          className="inline-flex items-center gap-1.5 rounded-fd-sm border border-fd-border bg-transparent px-3 py-2 font-geist text-[13px] font-medium text-fd-text-muted transition-colors hover:bg-fd-surface-1 hover:text-fd-text"
        >
          <ArrowLeft className="size-3.5" aria-hidden="true" />
          Back to the primer
        </button>
      </div>
    </motion.div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Surface: compact setup card after workspace confirmation
// ────────────────────────────────────────────────────────────────────────────

function CompactInstalledSurface({
  reduceMotion,
  onExpand,
  onReset,
  onStartModuleOne,
}: {
  reduceMotion: boolean;
  onExpand: () => void;
  onReset: () => void;
  onStartModuleOne?: () => void;
}) {
  return (
    <section
      aria-labelledby="aios-compact-heading"
      className="relative mb-6"
    >
      <motion.div
        initial={reduceMotion ? false : { opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ type: 'spring', stiffness: 140, damping: 22 }}
        className="relative isolate overflow-hidden rounded-[12px] border border-fd-border-strong/40 bg-fd-paper px-4 py-4 shadow-[0_14px_30px_-24px_rgba(20,16,8,0.2)] sm:px-5"
      >
        <div
          aria-hidden="true"
          className="absolute inset-x-0 top-0 h-[5px] bg-fd-accent"
        />
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex min-w-0 items-start gap-3">
            <RaceBib n="05" tone="race" size="sm" />
            <div className="min-w-0">
              <Eyebrow className="mb-1 block text-fd-accent-deep">workspace ready</Eyebrow>
              <h2
                id="aios-compact-heading"
                className="font-geist text-[18px] font-medium leading-[1.15] tracking-[-0.01em] text-fd-text"
              >
                Business workspace is set up.
              </h2>
              <p className="mt-1 max-w-[58ch] text-[13.5px] leading-[1.45] text-fd-text-muted">
                The onboarding guide is saved here if you need to review the setup, create a
                fresh workspace, or move the folder later.
              </p>
            </div>
          </div>
          <div className="flex shrink-0 flex-wrap items-center gap-2">
            {onStartModuleOne ? (
              <button
                type="button"
                onClick={onStartModuleOne}
                className="inline-flex items-center gap-1.5 rounded-fd-sm bg-fd-accent px-4 py-2 font-geist text-[13px] font-semibold text-[oklch(0.16_0.005_95)] shadow-[0_1px_0_rgba(0,0,0,0.04)] transition-transform hover:-translate-y-px active:translate-y-0"
              >
                <BookOpen className="size-3.5" aria-hidden="true" />
                Start Module 1
              </button>
            ) : null}
            <button
              type="button"
              onClick={onExpand}
              className="inline-flex items-center gap-1.5 rounded-fd-sm border border-fd-border bg-fd-surface-1 px-3 py-2 font-geist text-[13px] font-medium text-fd-text transition-colors hover:bg-fd-surface-2"
            >
              <ChevronDown className="size-3.5" aria-hidden="true" />
              View setup guide
            </button>
            <button
              type="button"
              onClick={onReset}
              className="inline-flex items-center gap-1.5 rounded-fd-sm border border-fd-border bg-transparent px-3 py-2 font-geist text-[13px] font-medium text-fd-text-muted transition-colors hover:bg-fd-surface-1 hover:text-fd-text"
            >
              <RotateCw className="size-3.5" aria-hidden="true" />
              Fresh setup
            </button>
          </div>
        </div>
      </motion.div>
    </section>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Surface: setup confirmed (workspace is ready)
// ────────────────────────────────────────────────────────────────────────────

function InstalledSurface({
  reduceMotion,
  onCollapse,
  onReset,
  onStartModuleOne,
}: {
  reduceMotion: boolean;
  onCollapse: () => void;
  onReset: () => void;
  onStartModuleOne?: () => void;
}) {
  return (
    <motion.div
      initial={reduceMotion ? false : { opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ type: 'spring', stiffness: 140, damping: 22 }}
    >
      <Eyebrow className="mb-2 block text-fd-accent-deep">workspace ready</Eyebrow>
      <h3 className="font-geist text-[26px] font-medium leading-[1.1] tracking-[-0.02em] text-fd-text">
        Start Module 1 next.
      </h3>
      <p className="mt-3 max-w-[55ch] text-[15px] leading-[1.55] text-fd-text-muted">
        Your workspace is ready. Module 1 explains what you just set up, how it fits
        the Build Room, and when to run the welcome conversation.
      </p>

      <ol className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-3">
        <InstalledStep
          n="01"
          title="Workspace is ready"
          body="AI Acquisition OS now lives inside the business folder you chose. Reopen that folder later from Claude Code or Codex."
        />
        <InstalledStep
          n="02"
          title="Module 1 sets the scene"
          body="The Shift explains what the workspace is for before you start asking the AI Co-Founder to do work."
        />
        <InstalledStep
          n="03"
          title="Fresh chat, then /welcome"
          body="Slash skills are loaded when a new AI app session starts. After Module 1, open a fresh chat in this same folder before running /welcome."
        />
      </ol>

      <div className="mt-5 rounded-[10px] border border-fd-border-strong/40 bg-fd-accent-soft px-4 py-3.5">
        <span className="block text-[11px] font-medium uppercase tracking-[0.16em] text-fd-accent-deep">
          This stays here
        </span>
        <p className="mt-1.5 text-[13.5px] leading-[1.5] text-fd-text">
          This setup card stays in your Build Room. Use it again if you ever want to create a
          fresh workspace, move the folder, or reset your working copy.
        </p>
      </div>

      <div className="mt-6 flex flex-wrap items-center justify-between gap-3">
        <p className="font-caveat text-[16px] leading-[1.15] text-fd-text-muted">
          Start with the lesson. The workspace will make more sense after the scene is set.
        </p>
        <div className="flex flex-wrap items-center gap-2">
          {onStartModuleOne ? (
            <button
              type="button"
              onClick={onStartModuleOne}
              className="inline-flex items-center gap-1.5 rounded-fd-sm bg-fd-accent px-4 py-2 font-geist text-[13px] font-semibold text-[oklch(0.16_0.005_95)] shadow-[0_1px_0_rgba(0,0,0,0.04)] transition-transform hover:-translate-y-px active:translate-y-0"
            >
              <BookOpen className="size-3.5" aria-hidden="true" />
              Start Module 1
            </button>
          ) : null}
          <button
            type="button"
            onClick={onCollapse}
            className="inline-flex items-center gap-1.5 rounded-fd-sm border border-fd-border bg-fd-surface-1 px-3 py-2 font-geist text-[13px] font-medium text-fd-text transition-colors hover:bg-fd-surface-2"
          >
            <ChevronUp className="size-3.5" aria-hidden="true" />
            Minimise setup guide
          </button>
          <button
            type="button"
            onClick={onReset}
            className="inline-flex items-center gap-1.5 rounded-fd-sm border border-fd-border bg-transparent px-3 py-2 font-geist text-[13px] font-medium text-fd-text-muted transition-colors hover:bg-fd-surface-1 hover:text-fd-text"
          >
            <RotateCw className="size-3.5" aria-hidden="true" />
            Fresh setup
          </button>
        </div>
      </div>
    </motion.div>
  );
}

function InstalledStep({
  n,
  title,
  body,
}: {
  n: string;
  title: string;
  body: string;
}) {
  return (
    <li className="rounded-[10px] border border-fd-border bg-fd-surface-1/40 p-3">
      <RaceBib n={n} tone="race" size="sm" />
      <p className="mt-2 font-geist text-[13.5px] font-medium leading-[1.25] text-fd-text">
        {title}
      </p>
      <p className="mt-1.5 text-[12.5px] leading-[1.45] text-fd-text-muted">{body}</p>
    </li>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Progress rail (right side of the card)
// ────────────────────────────────────────────────────────────────────────────

function ProgressRail({
  cardState,
  primerIndex,
}: {
  cardState: CardState;
  primerIndex: number;
}) {
  const steps = [
    { id: 'welcome', label: 'Welcome video' },
    { id: 'ai-app', label: 'AI app ready' },
    { id: 'workspace', label: 'Workspace setup' },
    { id: 'command', label: 'Setup message' },
    { id: 'module', label: 'Start Module 1' },
  ];

  const activeStep = (() => {
    if (cardState === 'installed') return 4;
    if (cardState === 'command' || cardState === 'fetching-token' || cardState === 'token-error')
      return 3;
    if (cardState === 'primer') return Math.min(primerIndex, 2);
    return 0;
  })();

  return (
    <ol className="space-y-3">
      {steps.map((step, i) => {
        const state: 'done' | 'active' | 'pending' =
          i < activeStep ? 'done' : i === activeStep ? 'active' : 'pending';
        return (
          <li key={step.id} className="flex items-start gap-3">
            <div
              aria-hidden="true"
              className={[
                'mt-[2px] inline-flex size-6 shrink-0 items-center justify-center rounded-[6px] border font-mono text-[10px] font-semibold tracking-[0.06em]',
                state === 'done'
                  ? 'border-fd-accent-deep bg-fd-accent text-[oklch(0.16_0.005_95)]'
                  : state === 'active'
                    ? 'border-fd-text bg-fd-bg text-fd-text shadow-[2px_2px_0_0_var(--fd-text)]'
                    : 'border-fd-border bg-transparent text-fd-text-subtle',
              ].join(' ')}
            >
              {String(i + 1).padStart(2, '0')}
            </div>
            <div className="grow">
              <span
                className={[
                  'block text-[13px] leading-[1.3]',
                  state === 'pending' ? 'text-fd-text-subtle' : 'text-fd-text',
                  state === 'active' ? 'font-medium' : 'font-normal',
                ].join(' ')}
              >
                {step.label}
              </span>
              {cardState === 'primer' && i === activeStep && state === 'active' && (
                <span className="mt-0.5 block font-mono text-[10px] uppercase tracking-[0.16em] text-fd-text-subtle">
                  screen {String(primerIndex + 1).padStart(2, '0')} of{' '}
                  {String(workspacePrimerScreens.length).padStart(2, '0')}
                </span>
              )}
            </div>
          </li>
        );
      })}
    </ol>
  );
}

function formatExpiryDuration(seconds: number) {
  if (!Number.isFinite(seconds) || seconds <= 0) return 'soon';
  const hours = Math.floor(seconds / 3600);
  if (hours >= 48 && hours % 24 === 0) return `${hours / 24} days`;
  if (hours >= 2) return `${hours} hours`;
  if (hours === 1) return '1 hour';
  const minutes = Math.max(1, Math.floor(seconds / 60));
  return minutes === 1 ? '1 minute' : `${minutes} minutes`;
}

function railNoteFor(cardState: CardState, primerIndex: number): string {
  if (cardState === 'installed') return 'Workspace is ready. Start a fresh AI app session before /welcome.';
  if (cardState === 'command') return 'Paste the message into your AI app, then come back when it says ready.';
  if (cardState === 'token-error') return "It's almost always a quick retry.";
  if (cardState === 'fetching-token') return 'Just a moment.';
  switch (primerIndex) {
    case 0:
      return 'Start with the welcome, then setup stays simple.';
    case 1:
      return 'Use one AI app. You do not need both to begin.';
    case 2:
      return 'Open your business folder now; Module 1 explains the workspace next.';
    default:
      return "One setup message and you're in.";
  }
}

function currentHref() {
  if (typeof window === 'undefined') return '/';
  return window.location.href;
}
